import React, { useState, useEffect, useRef, useCallback } from 'react';

const STREAM_STATE = {
  LIVE: 'LIVE',
  NEVER_STARTED: 'NEVER_STARTED',
  STOPPED: 'STOPPED'
};

const calculateDayLabel = () => {
  return 'Day 0 of 28';
};

const getStreamState = (streamStatus) => {
  if (!streamStatus) return null;
  if (streamStatus.isLive) return STREAM_STATE.LIVE;
  if (!streamStatus.hasEverBeenLive) return STREAM_STATE.NEVER_STARTED;
  return STREAM_STATE.STOPPED;
};

const getStreamMessage = (streamState) => {
  switch (streamState) {
    case STREAM_STATE.NEVER_STARTED:
      return '28 days · $28 · One chance';
    case STREAM_STATE.STOPPED:
      return "Tech's is being tech..\nThanks for being patient with us :)";
    case STREAM_STATE.LIVE:
    default:
      return null;
  }
};

const TestLiveScreen = () => {
  const [streamStatus, setStreamStatus] = useState(null);
  const [dayLabel, setDayLabel] = useState(calculateDayLabel());
  const iframeRef = useRef(null);
  const API_URL = process.env.REACT_APP_API_URL || '';

  // Plain Castr embed URL - no extra params
  const CASTR_PLAYER_URL = 'https://player.castr.com/live_73895e20035211f1b29097594b1c1d66';

  const streamState = getStreamState(streamStatus);
  const streamMessage = getStreamMessage(streamState);

  const fetchStreamStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stream/status`);
      const data = await response.json();
      setStreamStatus(data);
    } catch (err) {
      console.error('Error fetching stream status:', err);
    }
  }, [API_URL]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDayLabel(calculateDayLabel());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchStreamStatus();
    const interval = setInterval(fetchStreamStatus, 15000);
    return () => clearInterval(interval);
  }, [fetchStreamStatus]);

  return (
    <div style={styles.container}>
      {/* TEST PAGE INDICATOR */}
      <div style={styles.testBadge}>TEST PAGE</div>

      {dayLabel && (
        <div style={styles.topBar}>
          <div style={styles.dayCounter}>{dayLabel}</div>
        </div>
      )}

      {streamMessage && (
        <div style={styles.centerNotification} className="center-notification">
          <div style={streamState === STREAM_STATE.NEVER_STARTED ? styles.neverStartedText : styles.notificationText}>
            {streamMessage}
          </div>
        </div>
      )}

      {/* Plain Castr embed - matching provided embed code */}
      <div style={styles.playerContainer}>
        <iframe
          ref={iframeRef}
          src={CASTR_PLAYER_URL}
          style={styles.player}
          frameBorder="0"
          allowFullScreen
          title="Billy 28 Live Stream - Test"
        />
      </div>

      {/* Status Bar */}
      <div style={styles.statusBar}>
        <div style={styles.statusBarContent}>
          <div style={styles.statusInfo}>
            <span>
              Powered by <a href="https://FullAccess.Live" target="_blank" rel="noopener noreferrer" style={styles.statusLink}>FullAccess.Live</a>. All rights reserved. Contact us via - <a href="mailto:support@fullaccess.live" style={styles.statusLink}>support@fullaccess.live</a>
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @media (orientation: landscape) and (max-height: 500px) {
          .center-notification {
            bottom: 15% !important;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    position: 'relative',
    width: '100vw',
    height: '100vh',
    backgroundColor: '#000000',
    color: '#ffffff',
    fontFamily: 'Roboto, sans-serif',
    overflow: 'hidden'
  },
  testBadge: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    zIndex: 20,
    backgroundColor: '#ff9800',
    color: '#000',
    padding: '6px 14px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 'bold',
    letterSpacing: '1px'
  },
  topBar: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    zIndex: 10,
    pointerEvents: 'none'
  },
  dayCounter: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: 'bold',
    backdropFilter: 'blur(10px)',
    pointerEvents: 'auto'
  },
  centerNotification: {
    position: 'absolute',
    bottom: '10%',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 15,
    pointerEvents: 'none',
    width: '90%',
    maxWidth: '500px',
    textAlign: 'center'
  },
  notificationText: {
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    color: '#ffffff',
    padding: '12px 20px',
    borderRadius: '12px',
    fontSize: 'clamp(14px, 3.5vw, 18px)',
    fontWeight: '500',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    whiteSpace: 'pre-line'
  },
  neverStartedText: {
    color: '#454751',
    fontSize: 'clamp(14px, 4.2vw, 18px)',
    fontWeight: '500',
    fontFamily: "'Roboto', sans-serif"
  },
  playerContainer: {
    position: 'relative',
    width: '100%',
    height: 'calc(100% - 50px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  player: {
    width: '100%',
    height: '100%',
    border: 'none'
  },
  statusBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '50px',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    backdropFilter: 'blur(10px)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    zIndex: 10
  },
  statusBarContent: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  statusInfo: {
    fontSize: '14px',
    color: '#888'
  },
  statusLink: {
    color: '#888',
    textDecoration: 'underline'
  }
};

export default TestLiveScreen;
